import * as React from "react";

import { cn } from "@/common/helpers/utils";

type CardDepth = 0 | 1 | 2;

type CardProps = Omit<React.ComponentProps<"div">, "title"> & {
  depth?: CardDepth;
};

const CARD_DEPTH_CLASSES: Record<CardDepth, string> = {
  0: "bg-card",
  1: "bg-[color-mix(in_srgb,var(--card)_97%,white)] dark:bg-[color-mix(in_srgb,var(--card)_96%,white)]",
  2: "bg-[color-mix(in_srgb,var(--card)_94%,white)] dark:bg-[color-mix(in_srgb,var(--card)_92%,white)]",
};

function Card({ className, depth = 0, ...props }: CardProps) {
  return (
    <div
      data-slot="card"
      className={cn(
        CARD_DEPTH_CLASSES[depth],
        "text-card-foreground border-border relative flex flex-col gap-4 rounded-xl border py-4",
        className,
      )}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header flex flex-col gap-1.5 px-5",
        "has-data-[slot=card-action]:grid has-data-[slot=card-action]:auto-rows-min has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-action]:items-start [.border-b]:pb-6",
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-title" className={cn("leading-none font-semibold", className)} {...props} />;
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-description" className={cn("text-muted-foreground text-sm", className)} {...props} />;
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn("col-start-2 row-span-2 row-start-1 self-start justify-self-end", className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-content" className={cn("px-5", className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="card-footer" className={cn("flex items-center px-5 [.border-t]:pt-6", className)} {...props} />
  );
}

export { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };
